// resources/js/pixel.ts
import type { router as InertiaRouter } from '@inertiajs/react';

declare global {
  interface Window {
    fbq?: (...args: any[]) => void;
    _fbq?: any;
  }
}

type AdvancedMatchingPayload = Record<string, string>;

type AuthenticatedUser = {
  id?: string | number;
  email?: string | null;
  name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  customer_phone?: string | null;
  phone?: string | null;
  external_id?: string | number | null;
};

type PagePropsAuth = {
  auth?: {
    user?: AuthenticatedUser | null;
  };
} & Record<string, unknown>;

type InertiaPagePayload = {
  props?: PagePropsAuth;
};

let cachedPageProps: PagePropsAuth | null = null;
let cachedAdvancedMatching: AdvancedMatchingPayload | null = null;
let hasHydratedInitialProps = false;
let lastPayloadHadValues = false;

const hasAdvancedMatchingValues = (
  payload?: AdvancedMatchingPayload | null,
): payload is AdvancedMatchingPayload => {
  if (!payload) return false;
  for (const key in payload) {
    if (Object.prototype.hasOwnProperty.call(payload, key)) {
      return true;
    }
  }
  return false;
};

const sanitizeEmail = (value?: string | null): string | null => {
  if (!value) return null;
  const normalized = value.trim().toLowerCase();
  return normalized || null;
};

const sanitizePhone = (value?: string | null): string | null => {
  if (!value) return null;
  const digits = value.replace(/\D/g, '');
  return digits.length >= 6 ? digits : null;
};

const stripAccents = (value: string): string => {
  try {
    return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  } catch {
    return value;
  }
};

const sanitizeName = (value?: string | null): string | null => {
  if (!value) return null;
  const processed = stripAccents(value.trim().toLowerCase()).replace(/[^a-z]/g, '');
  return processed || null;
};

const sanitizeGenericString = (value?: string | number | null): string | null => {
  if (value === undefined || value === null) return null;
  const str = String(value).trim();
  return str || null;
};

const splitFullName = (value?: string | null): { firstName: string | null; lastName: string | null } => {
  if (!value) return { firstName: null, lastName: null };
  const trimmed = value.trim();
  if (!trimmed) return { firstName: null, lastName: null };
  const parts = trimmed.split(/\s+/);
  const firstName = parts[0] ?? null;
  const lastName = parts.length > 1 ? parts.slice(1).join(' ') : null;
  return { firstName, lastName };
};

const buildAdvancedMatchingFromUser = (user?: AuthenticatedUser | null): AdvancedMatchingPayload | null => {
  if (!user) return null;

  const payload: AdvancedMatchingPayload = {};

  const email = sanitizeEmail(user.email ?? null);
  if (email) payload.em = email;

  const phone = sanitizePhone(user.customer_phone ?? user.phone ?? null);
  if (phone) payload.ph = phone;

  const nameParts = splitFullName(user.name ?? null);
  const firstName = sanitizeName(user.first_name ?? nameParts.firstName);
  if (firstName) payload.fn = firstName;

  const lastName = sanitizeName(user.last_name ?? nameParts.lastName);
  if (lastName) payload.ln = lastName;

  const externalId = sanitizeGenericString(user.external_id ?? user.id ?? null);
  if (externalId) payload.external_id = externalId;

  return hasAdvancedMatchingValues(payload) ? payload : null;
};

const buildAdvancedMatchingPayload = (props: PagePropsAuth | null): AdvancedMatchingPayload | null => {
  if (!props || typeof props !== 'object') return null;
  return buildAdvancedMatchingFromUser(props.auth?.user ?? null);
};

const setCurrentPageProps = (props: PagePropsAuth | null): void => {
  cachedPageProps = props;
  cachedAdvancedMatching = buildAdvancedMatchingPayload(props);
};

const readPropsFromDom = (): PagePropsAuth | null => {
  if (typeof document === 'undefined') return null;
  const pageElement = document.querySelector<HTMLElement>('[data-page]');
  if (!pageElement) return null;
  const pageData = pageElement.getAttribute('data-page');
  if (!pageData) return null;
  try {
    const parsed = JSON.parse(pageData) as InertiaPagePayload;
    if (parsed && typeof parsed === 'object' && parsed.props && typeof parsed.props === 'object') {
      return parsed.props;
    }
  } catch {
    // Silently ignore parsing errors in production
  }
  return null;
};

const ensureInitialPropsHydrated = (): void => {
  if (hasHydratedInitialProps) return;
  hasHydratedInitialProps = true;
  setCurrentPageProps(readPropsFromDom());
};

const getAdvancedMatchingPayload = (): AdvancedMatchingPayload | null => {
  ensureInitialPropsHydrated();
  return cachedAdvancedMatching;
};

const updateCurrentPageProps = (props: unknown): AdvancedMatchingPayload | null => {
  if (props && typeof props === 'object') {
    setCurrentPageProps(props as PagePropsAuth);
  } else {
    setCurrentPageProps(null);
  }
  return cachedAdvancedMatching;
};

const applyAdvancedMatchingToPixel = (payload?: AdvancedMatchingPayload | null): void => {
  if (typeof window === 'undefined' || !window.fbq) return;
  const data = payload ?? getAdvancedMatchingPayload();
  if (hasAdvancedMatchingValues(data)) {
    window.fbq('set', 'userData', data);
    lastPayloadHadValues = true;
  } else if (lastPayloadHadValues) {
    window.fbq('set', 'userData', {});
    lastPayloadHadValues = false;
  }
};

function loadPixelScript() {
  (function (f: any, b: Document, e: string, v: string, n: any, t: HTMLScriptElement, s: Element | null) {
    if (f.fbq) return;
    n = f.fbq = function () {
      // @ts-ignore
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    // @ts-ignore
    if (!f._fbq) f._fbq = n;
    // @ts-ignore
    n.push = n; n.loaded = true; n.version = '2.0'; n.queue = [];
    t = b.createElement(e) as HTMLScriptElement; t.async = true; t.src = v;
    s = b.getElementsByTagName(e)[0]; s?.parentNode?.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js', null as any, null as any, null);
}

export function initFacebookPixel(router: typeof InertiaRouter) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  const id = document.querySelector<HTMLMetaElement>('meta[name="facebook-pixel-id"]')?.content;
  if (!id) return;

  ensureInitialPropsHydrated();

  // Load pixel immediately (Meta-compliant approach)
  loadPixelScript();

  const payload = getAdvancedMatchingPayload();
  if (hasAdvancedMatchingValues(payload)) {
    window.fbq?.('init', id, payload);
    lastPayloadHadValues = true;
  } else {
    window.fbq?.('init', id);
    lastPayloadHadValues = false;
  }
  window.fbq?.('track', 'PageView'); // initial load

  router.on('success', (event: any) => {
    const payload = updateCurrentPageProps(event.detail?.page?.props);
    applyAdvancedMatchingToPixel(payload);
  });

  // Track setiap navigasi Inertia yang selesai
  router.on('finish', (event: any) => {
    if (event.detail?.visit?.completed) {
      window.fbq?.('track', 'PageView');
      applyAdvancedMatchingToPixel();
    }
  });
}
